import {
  LinkAccountSubtype,
  LinkAccountType,
  LinkAccountVerificationStatus,
  LinkErrorCode,
  LinkErrorType,
  LinkExit,
  LinkExitMetadataStatus,
  LinkSuccess,
} from "react-native-plaid-link-sdk"
import {
  PlaidLinkOnEventMetadata,
  PlaidLinkOptions,
  usePlaidLink as useLegacyPlaidLink,
} from "react-plaid-link"
import { MOCK } from "../../constants"
import { retrieveValue, Sentry } from "../../utils"
import { useContext, useEffect, useRef } from "react"
import { PlaidContext } from "../../context/plaidContext"
import { usePostHog } from "../usePosthog"

function getPlaidConfig(props: {
  onSuccess: (success: LinkSuccess) => void
  onExit: (exit: LinkExit) => void
  token?: string
}): PlaidLinkOptions {
  return {
    onSuccess: (public_token, metadata) =>
      props.onSuccess({
        publicToken: public_token ?? "",
        metadata: {
          accounts: metadata.accounts.map((a) => ({
            id: a.id,
            name: a.name,
            mask: a.mask ?? undefined,
            type: a.type as LinkAccountType,
            subtype: a.subtype as LinkAccountSubtype,
            verificationStatus: a.verification_status as LinkAccountVerificationStatus,
          })),
          linkSessionId: metadata.link_session_id,
          institution: metadata.institution
            ? {
                id: metadata.institution.institution_id,
                name: metadata.institution.name,
              }
            : undefined,
        },
      }),
    onExit: (err, metadata) => {
      return props.onExit({
        error: err
          ? {
              errorCode: err.error_code as LinkErrorCode,
              errorType: err.error_type as LinkErrorType,
              errorMessage: err.error_message ?? "",
              displayMessage: err.display_message ?? undefined,
            }
          : undefined,
        metadata: {
          linkSessionId: metadata.link_session_id,
          institution: metadata.institution
            ? {
                id: metadata.institution.institution_id,
                name: metadata.institution.name,
              }
            : undefined,
          requestId: metadata.request_id,
          status: metadata.status as LinkExitMetadataStatus,
        },
      })
    },
    token: props.token || "",
  }
}

export function usePlaidLink(props: {
  token: string
  onSuccess: (success: LinkSuccess) => void
  onExit: (exit: LinkExit) => void
  /** The URL the bank redirected back to; re-opens the same Link session after OAuth. */
  receivedRedirectUri?: string | null
}) {
  const { appendEvent } = useContext(PlaidContext)
  const posthog = usePostHog()
  const isOAuthReturn = !!props.receivedRedirectUri
  const { open, ready } = useLegacyPlaidLink({
    ...getPlaidConfig(props),
    token: props.token,
    receivedRedirectUri: props.receivedRedirectUri ?? undefined,
    onEvent: (eventName: string, metadata: PlaidLinkOnEventMetadata) => {
      appendEvent(`Fallback event ${eventName}`)
      Sentry.addBreadcrumb({
        category: "plaid",
        message: `Fallback event ${eventName}`,
        level: "info",
        data: {
          viewName: metadata.view_name,
          institutionId: metadata.institution_id,
          institutionName: metadata.institution_name,
          errorCode: metadata.error_code,
          exitStatus: metadata.exit_status,
          linkSessionId: metadata.link_session_id,
          isOAuthReturn,
        },
      })
      if (eventName === "OPEN_OAUTH") {
        posthog.capture("plaid_oauth_opened", {
          institution_id: metadata.institution_id,
          institution_name: metadata.institution_name,
        })
      }
    },
  })

  const openCbRef = useRef<typeof open>(open)
  const openWhenReady = useRef(false)

  useEffect(() => {
    openCbRef.current = open
  }, [open])

  // An OAuth return must not open before Link has loaded the redirect URI.
  useEffect(() => {
    if (ready && openWhenReady.current) {
      openWhenReady.current = false
      open()
    }
  }, [open, ready])

  const startLink = async () => {
    if (isOAuthReturn) {
      appendEvent("Fallback OAuth return")
      if (ready) {
        open()
      } else {
        openWhenReady.current = true
      }
      return
    }
    const demo = (await retrieveValue("mode")) === "demo"
    if (MOCK || demo) {
      props.onSuccess({
        publicToken: props.token,
        metadata: {
          institution: { id: "", name: "" },
          accounts: [],
          linkSessionId: "",
        },
      })
    }
    setTimeout(() => {
      appendEvent(`Fallback triggered, ${openCbRef.current.toString()}`)
      openCbRef.current()
    }, 500)
  }

  return { startLink }
}

export function dismissLink() {
  // noop
}
